import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Tour } from '@reactour/tour';
import classnames from 'classnames';
import { filterWBSData, assignDescendantActivityCounts, filterActivityCodeTree } from '../util/FilterLogic.jsx';
import FlyoutLeft from '../components/FlyoutLeft.jsx';
import FlyoutRight from '../components/FlyoutRight.jsx';
import MainTopNav from '../components/MainTopNav.jsx';
import WBSTree from '../components/WBSTree.jsx';
import GroupTree from '../components/GroupTree.jsx';
import FeedList from '../components/FeedList.jsx';
import MapView from '../components/MapView.jsx';
import MapUnavailable from '../components/MapUnavailable.jsx';
import CalendarView from '../components/CalendarView.jsx';
import ResourceGraphView from '../components/ResourceGraphView.jsx';
import MapRangeSlider from '../components/MapRangeSlider.jsx';
import { projectHasKml } from '../util/projectMap.js';
import { resolveTimescale } from '../util/timescale.js';
import { focusProject } from '../util/openProject.js';
import { projectToActivate, readStoredOpenDatasets } from '../context/openDatasets.js';
import Utils from '../util/Utils.jsx';
import { trackPageView } from '../util/Analytics.jsx';
import AddProjectModal from '../components/AddProjectModal.jsx';
import LayoutChangedModal from '../components/LayoutChangedModal.jsx';
import AiInsightsPanel from '../components/AiInsightsPanel.jsx';

import { useStateValue } from '../context/state.jsx';
import { setTourInfo } from '../context/tourInfo.js';

const tourStyles = {
	popover: (base) => ({
		...base,
		borderRadius: 6
	}),
	maskArea: (base) => ({
		...base,
		rx: 6
	})
};

const MainPage = ({
	setMapPinMyLocation,
	setProximityDistance,
	proximityDistance,
	mapPinMyLocation,
	mapPinPickMode,
	setMapPinPickMode
}) => {
	const location = useLocation();
	const matchDeep = useMatch('/app/dataset/:datasetId/*');
	const matchBase = useMatch('/app/dataset/:datasetId');
	const datasetId = matchDeep?.params?.datasetId ?? matchBase?.params?.datasetId ?? null;
	const [{
		datasets,
		projectActivities,
		projectFilter,
		wbsData,
		projectActivityCodesTree,
		projectViewType,
		projectGroupBy,
		projectGroupByActivityCode,
		projectMapGroupBy,
		tourInfo,
		isBulkActive,
		bulkActivities,
		filteredActivities,
		filteredActivitiesMap,
		isOnline,
		showAddProjectModal
	}, dispatch] = useStateValue();
	const navigate = useNavigate();
	const activatingProject = useRef('');

	const [isFlyoutLeftActive, setIsFlyoutLeftActive] = useState(datasetId === null);
	const [isFlyoutRightActive, setIsFlyoutRightActive] = useState(false);
	const viewsButtonRef = useRef(null);
	const projectsButtonRef = useRef(null);
	const flyoutRightCloseRef = useRef(null);
	const flyoutLeftCloseRef = useRef(null);
	const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
	const [tourId, setTourId] = useState(null);
	const [tourSteps, setTourSteps] = useState([]);
	const [isTourOpen, setIsTourOpen] = useState(false);
	const [currentTourStep, setCurrentTourStep] = useState(0);

	const closeTour = () => {
		setIsTourOpen(false);
		dispatch(setTourInfo(tourId));
		Utils.ajax('updateTourInfo', { tourId }, dispatch);
	};

	const handleSetTourOpen = (value) => {
		const next = typeof value === 'function' ? value(isTourOpen) : value;
		if (next) {
			setIsTourOpen(true);
		} else {
			closeTour();
		}
	};

	useEffect(() => {
		trackPageView();
	}, []);

	useEffect(() => {
		const id = projectToActivate(datasetId, readStoredOpenDatasets());
		if (!id || String(datasetId) === id) {
			activatingProject.current = '';
			return;
		}
		if (activatingProject.current === id) {
			return;
		}
		activatingProject.current = id;
		focusProject({ dispatch, navigate, datasetId: id });
	}, [datasetId, dispatch, navigate]);

	// Keep left nav closed when landing on a project via invite, verify, or share links.
	useEffect(() => {
		if (datasetId) {
			setIsFlyoutLeftActive(false);
		}
	}, [datasetId]);

	const flyoutOpen = isFlyoutLeftActive || isFlyoutRightActive;

	const closeFlyouts = useCallback((restoreFocus = true) => {
		const restoreViews = restoreFocus && isFlyoutRightActive;
		const restoreProjects = restoreFocus && isFlyoutLeftActive && !isFlyoutRightActive;
		setIsFlyoutLeftActive(false);
		setIsFlyoutRightActive(false);
		if (restoreViews && viewsButtonRef.current && typeof viewsButtonRef.current.focus === 'function') {
			viewsButtonRef.current.focus();
		} else if (restoreProjects && projectsButtonRef.current && typeof projectsButtonRef.current.focus === 'function') {
			projectsButtonRef.current.focus();
		}
	}, [isFlyoutLeftActive, isFlyoutRightActive]);

	useEffect(() => {
		if (!flyoutOpen) {
			return undefined;
		}
		const onKeyDown = event => {
			if (event.key === 'Escape') {
				event.preventDefault();
				closeFlyouts(true);
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => {
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [flyoutOpen, closeFlyouts]);

	useEffect(() => {
		if (isFlyoutRightActive && flyoutRightCloseRef.current && typeof flyoutRightCloseRef.current.focus === 'function') {
			flyoutRightCloseRef.current.focus();
		} else if (isFlyoutLeftActive && flyoutLeftCloseRef.current && typeof flyoutLeftCloseRef.current.focus === 'function') {
			flyoutLeftCloseRef.current.focus();
		}
	}, [isFlyoutLeftActive, isFlyoutRightActive]);

	useEffect(() => {
		if (isFlyoutRightActive && !datasetId) {
			setIsFlyoutRightActive(false);
		}
	}, [isFlyoutRightActive, datasetId]);

	useEffect(() => {
		// Manage the tour on the main page
		let nextTourId = null,
			nextTourSteps = [];

		const path = location.pathname;

		if (path === '/app/') {
			nextTourId = 1;
			nextTourSteps = [{
				selector: '.addProject',
				content: 'You can add projects here once you export your file from P6 as an .xer file.'
			}, {
				selector: '.flyoutLeft .panel .list-group',
				content: 'This is where you\'ll select a project to load.'
			}];
		} else if (path.indexOf('/app/dataset/') > -1 && path.indexOf('/activity/') === -1) {
			nextTourId = 2;
			nextTourSteps = [
				{
					selector: '.views',
					content: 'Your layout management, grouping, and filtering will be done here.'
				}, {
					selector: '.layoutPanel',
					content: 'You can manage layouts using this panel. A layout is a saved view along with any chosen filters.',
					action: () => {
						// Without this if statement, the action changes the state causes an infinite re-render loop
						if (!isFlyoutRightActive) {
							setIsFlyoutRightActive(true);
						}
					}
				}, {
					selector: '.layoutPanel button:nth-of-type(4)',
					content: 'Once you save a layout, you can share it. SiteWalker will generate a unique link and QR code for the recipient to load!'
				}, {
					selector: '.projectViewTypeList',
					content: 'Grouping: view projects as a WBS, a group list (date, float, resource, or an activity code), or by mapping if applicable.'
				}, {
					selector: '.filterByCodeTree',
					content: 'There are many filters you can use to limit the activities you see. One example is filtering by activity code.'
				}
			];
		}
		setTourId(nextTourId);
		setTourSteps(nextTourSteps);

		if (nextTourId && tourInfo && !(nextTourId in tourInfo)) {
			setCurrentTourStep(0);
			setIsTourOpen(true);
		}
	}, [location.pathname, tourInfo]);

	const storedDataset = datasetId && datasets ? datasets[datasetId] : null;
	const timescale = resolveTimescale(storedDataset, projectActivities);
	// A new object on every render retriggers the resource graph fetch, because
	// that fetch updates ajax state and renders this page again.
	const datasetInfo = useMemo(() => {
		if (!storedDataset) {
			return null;
		}
		return {
			...storedDataset,
			StartDate: timescale.start || storedDataset.StartDate,
			EndDate: timescale.end || storedDataset.EndDate
		};
	}, [storedDataset, timescale.start, timescale.end]);

	let mainDataComponent = null;
	if (datasetInfo) {
		if (projectViewType && filteredActivities && filteredActivitiesMap) {
			if (projectViewType === 'WBS List') {
				const filteredWBSData = filterWBSData(wbsData, filteredActivitiesMap);
				for (const w in filteredWBSData) {
					assignDescendantActivityCounts(filteredWBSData[w]);
				}
				mainDataComponent = <WBSTree
					wbsData={filteredWBSData}
					filteredActivities={filteredActivities}
					filteredActivitiesMap={filteredActivitiesMap}
					datasetInfo={datasetInfo}
					isBulkActive={isBulkActive}
					bulkActivities={bulkActivities} />;
			} else if (projectViewType === 'Group List') {
				const filteredActivityCodesTree = filterActivityCodeTree(projectActivityCodesTree, filteredActivitiesMap);
				for (const a in filteredActivityCodesTree) {
					assignDescendantActivityCounts(filteredActivityCodesTree[a]);
				}
				mainDataComponent = <GroupTree
					filteredActivities={filteredActivities}
					filteredActivitiesMap={filteredActivitiesMap}
					activityCodesTree={filteredActivityCodesTree}
					groupBy={projectGroupBy}
					groupByActivityCode={projectGroupByActivityCode}
					datasetInfo={datasetInfo}
					isBulkActive={isBulkActive}
					bulkActivities={bulkActivities} />;
			} else if (projectViewType === 'Recent Feed') {
				mainDataComponent = <FeedList
					filteredActivities={filteredActivities}
					filteredActivitiesMap={filteredActivitiesMap}
					datasetInfo={datasetInfo}
					isBulkActive={isBulkActive}
					bulkActivities={bulkActivities} />;
			} else if (projectViewType === 'Map') {
				mainDataComponent = projectHasKml(datasetInfo)
					? (
						<MapView
							filteredActivities={filteredActivities}
							filteredActivitiesMap={filteredActivitiesMap}
							projectMapGroupBy={projectMapGroupBy}
							datasetInfo={datasetInfo}
							proximityDistance={proximityDistance}
							mapPinMyLocation={mapPinMyLocation}
							mapPinPickMode={mapPinPickMode}
							setMapPinMyLocation={setMapPinMyLocation}
							setMapPinPickMode={setMapPinPickMode} />
					)
					: <MapUnavailable />;
			} else if (projectViewType === 'Calendar') {
				mainDataComponent = <CalendarView
					filteredActivities={filteredActivities}
					datasetInfo={datasetInfo}
					projectFilter={projectFilter} />;
			} else if (projectViewType === 'Resource Graphs') {
				mainDataComponent = <ResourceGraphView
					filteredActivities={filteredActivities}
					datasetInfo={datasetInfo}
					projectFilter={projectFilter} />;
			} else {
				mainDataComponent = <div>{projectViewType} View coming soon!</div>;
			}
		}
	} else if (!isOnline) {
		// TODO: See comment about this in SWP - we want to alert there rather than here becuase you might be on ActivityPage when offline
		mainDataComponent = <div>You're offline and the current project has not been downloaded to your device. When online, go to Setup and select \'Allow Offline\' to download this project.</div>;
	}

	const mainPageClassNames = classnames('mainPage', {
		'bulk-active': isBulkActive,
		'map-active': projectViewType === 'Map',
		'calendar-active': projectViewType === 'Calendar',
		'resource-graph-active': projectViewType === 'Resource Graphs'
	});

	const mainDataComponentClassNames = classnames(
		'mainDataComponent belowTopNavContent', {
			flyoutRightActive: isFlyoutRightActive,
			flyoutLeftActive: isFlyoutLeftActive,
			isCapturingScreenshot
		}
	);

	const showListDateSlider = Boolean(
		datasetInfo
		&& projectViewType
		&& projectViewType !== 'Map'
		&& projectViewType !== 'Calendar'
		&& projectViewType !== 'Resource Graphs'
		&& datasetInfo.StartDate
		&& datasetInfo.EndDate
	);

	//TODO: See if the below comment is still true
	// This runs 5 times when loading /app, every time a flyout is opened/closed, and 3 times when Mult is clicked. I'm sure there's many more!
	//console.log('Ran MainPage');

	//TODO: Check whether all datasetId and currentDataset props below are necessary, or whether we can use `const { datasetId } = useParams();` in those components instead
	return (
		<div className={mainPageClassNames}>
			<div className={mainDataComponentClassNames}>
				{showListDateSlider
					? (
						<div className="listDateSliderContainer">
							<MapRangeSlider
								minDate={datasetInfo.StartDate}
								maxDate={datasetInfo.EndDate} />
						</div>
					)
					: null}
				{mainDataComponent}
			</div>

			<MainTopNav
				datasetId={datasetId}
				setIsFlyoutLeftActive={setIsFlyoutLeftActive}
				setIsFlyoutRightActive={setIsFlyoutRightActive}
				isFlyoutLeftActive={isFlyoutLeftActive}
				isFlyoutRightActive={isFlyoutRightActive}
				viewsButtonRef={viewsButtonRef}
				projectsButtonRef={projectsButtonRef}
				filteredActivities={filteredActivities}
				filteredActivitiesMap={filteredActivitiesMap}
				bulkActivities={bulkActivities} />

			{flyoutOpen
				? (
					<div
						className="flyoutDismiss active"
						onClick={() => closeFlyouts(true)}
						aria-hidden="true" />
				)
				: null}

			<FlyoutLeft
				datasets={datasets}
				currentDataset={datasetId}
				isActive={isFlyoutLeftActive}
				setIsFlyoutLeftActive={setIsFlyoutLeftActive}
				closeButtonRef={flyoutLeftCloseRef}
				onRequestClose={() => closeFlyouts(true)} />

			<FlyoutRight
				isActive={isFlyoutRightActive}
				setIsFlyoutRightActive={setIsFlyoutRightActive}
				closeButtonRef={flyoutRightCloseRef}
				onRequestClose={() => closeFlyouts(true)}
				setIsCapturingScreenshot={setIsCapturingScreenshot}
				datasetInfo={datasetInfo}
				projectFilter={projectFilter}
				projectViewType={projectViewType}
				setProximityDistance={setProximityDistance}
				setMapPinMyLocation={setMapPinMyLocation}
				mapPinMyLocation={mapPinMyLocation}
				mapPinPickMode={mapPinPickMode}
				setMapPinPickMode={setMapPinPickMode} />

			<AddProjectModal shown={showAddProjectModal} />

			<LayoutChangedModal datasetInfo={datasetInfo} />

			<AiInsightsPanel datasetId={datasetId} />

			{!isOnline || !tourSteps.length || !isTourOpen
				? null
				: <Tour
					key={`${isFlyoutRightActive}`}
					steps={tourSteps}
					currentStep={currentTourStep}
					setCurrentStep={setCurrentTourStep}
					setIsOpen={handleSetTourOpen}
					onClickClose={closeTour}
					onClickMask={() => {}}
					disableInteraction={true}
					badgeContent={({ currentStep, totalSteps }) => `${currentStep + 1} of ${totalSteps}`}
					styles={tourStyles}
					disabledActions={false}
					setDisabledActions={() => {}}
					setSteps={() => {}}
					meta=""
					setMeta={() => {}} />}
		</div>
	);
};
export default MainPage;

MainPage.propTypes = {
	setMapPinMyLocation: PropTypes.func,
	setProximityDistance: PropTypes.func,
	proximityDistance: PropTypes.number,
	mapPinMyLocation: PropTypes.object,
	mapPinPickMode: PropTypes.bool,
	setMapPinPickMode: PropTypes.func
};
